import {ApiGetAllTags} from "../../tags/api/testTags";
import {useAuth} from "../../../../hooks/useAuth";
import {APIPostCustomItemsGrades, APIPostCustomItemsSubjects} from "../../listing/api/customSubcategories";
import {getSubjectType} from "../../../../shared/types/subjectTypes";
import {getTestTypeId, TestType} from "../../../../shared/types/testTypes";
import {Dispatch, useCallback, useEffect, useState} from "react";
import {DEFAULT_GRADE_TYPES, getSchoolGradeTypes} from "../../../../shared/types/gradeTypes";
import {APIPostCategories, APIPostGrades} from "../api/createTest";
import {IFormCreateTestTraining} from "../components/_interfaces";
import {IUserTags} from "../../tags/components/UserTags";
import {getSubjects} from "../../../../utils/subjects";
import {SelectChangeEvent} from "@mui/material/Select";
import {getBacCategories} from "../utils/bacEvaluateCategories";
import {
    fetchCategories,
    getCategoriesIds,
    getDigitalLiteracyGradesIds,
} from "../utils/functions";
import {APIPostItem, APIPostItems} from "../api/createTestsTraining";

export interface LoadingState {
    loading: boolean;
    name: string;
}

interface UseTestFormDataFetchingOptions {
    type: TestType;
    state: IFormCreateTestTraining;
    dispatch: Dispatch<any>;
}

const UseTestFormDataFetching = ({
    type,
    state,
    dispatch} : UseTestFormDataFetchingOptions) => {
    const { update } = useAuth();
    const [loading, setLoading] = useState<LoadingState>({ loading: false, name: "" });
    const [tags, setTags] = useState<IUserTags[]>([]);
    const [subjects, setSubjects] = useState<any[]>(() => getSubjects("school"));
    const [grades, setGrades] = useState<any[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const [excludedItems, setExcludedItems] = useState<any[]>([]);


    useEffect(() => {
        let isMounted = true;

        const initFormData = async () => {
            setLoading({loading: false, name: "tags"});
            const fetchTags = ApiGetAllTags()
                .then((response) => {
                    if(!isMounted) return;
                    if(response.data.success) {
                        setTags(response.data.data.values);
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch(() => {
                    if(!isMounted) update("error", {show: true, code: "A001"});
                })
                .finally(() => {
                    if (isMounted) setLoading({loading: false, name: "tags"});
                });

            let fetchSubjects: Promise<void> | null = null;
            if (type === "customTest") {
                setLoading({ loading: true, name: "subjects" });
                fetchSubjects = APIPostCustomItemsSubjects()
                    .then((response) => {
                        if (!isMounted) return;
                        if (response.data.success) {
                            setSubjects(response.data.data.data.items);
                        } else {
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch(() => {
                        if (isMounted) update("error", { show: true, code: "A001" });
                    })
                    .finally(() => {
                        if (isMounted) setLoading({ loading: false, name: "subjects" });
                    });
            } else {
                setSubjects(getSubjectType(type));
            }

            let fetchGrades: Promise<void> | null = null;
            if (type === "digitalLiteracy" || type === "numericLiteracy") {
                setGrades(DEFAULT_GRADE_TYPES);
            } else if (type === "literacy" || type === "scientificLiteracy") {
                setLoading({ loading: true, name: "grade" });
                fetchGrades = APIPostGrades({ typeId: getTestTypeId(type) })
                    .then((response) => {
                        if (!isMounted) return;
                        if (response.data.success) {
                            setGrades(response.data.data.items);
                        } else {
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch(() => {
                        if (isMounted) update("error", { show: true, code: "A001" });
                    })
                    .finally(() => {
                        if (isMounted) setLoading({ loading: false, name: "grade" });
                    });
            }

            await Promise.allSettled([fetchTags, fetchSubjects, fetchGrades].filter(Boolean));
        }

        void initFormData();

        return () => {
            isMounted = false;
        };
    }, []);

    const setSubjectHandler = useCallback(
        async (event: SelectChangeEvent) => {
            const selectedSubject = event.target.value;
            const isBacOrEvaluate = type === "bac" || type === "evaluate";

            dispatch({
                type: "subject",
                payload: { value: selectedSubject, label: "subject" },
            });

            if (isBacOrEvaluate) {
                dispatch({
                    type: "categories",
                    payload: getBacCategories(selectedSubject),
                });
                return;
            }

            if (type === "school" || type === "training") {
                setGrades(getSchoolGradeTypes(Number(selectedSubject)));
                return;
            }

            setLoading({ loading: true, name: "grade" });
            try {
                const response =
                    type === "customTest"
                        ? await APIPostCustomItemsGrades()
                        : await APIPostGrades({ typeId: selectedSubject });

                if (response.data?.success) {
                    const itemsResult =
                        type === "customTest"
                            ? response.data.data.data.items
                            : response.data.data.items;
                    setGrades(itemsResult);
                } else {
                    update("error", { show: true, code: "A002" });
                }
            } catch {
                update("error", { show: true, code: "A001" });
            } finally {
                setLoading({ loading: false, name: "grade" });
            }
        },
        [type, dispatch, update]
    );

    const setGradeHandler = useCallback(
        async (event: SelectChangeEvent) => {
            const selectedGrade = Number(event.target.value);
            const isNumericLiteracy = type === "numericLiteracy";
            const digitalGradeId =
                type === "digitalLiteracy"
                    ? getDigitalLiteracyGradesIds(selectedGrade)
                    : undefined;

            dispatch({
                type: isNumericLiteracy ? "gradeNumericLiteracy" : "grade",
                payload: {
                    value: selectedGrade,
                    label: "grade",
                    ...(digitalGradeId !== undefined && { gradeId: digitalGradeId }),
                },
            });

            if (isNumericLiteracy) {
                setSubjects(
                    getSubjects(selectedGrade <= 4 ? "numericLiteracyPrimary" : "numericLiteracy")
                );
                return;
            }

            setLoading({ loading: true, name: "categories" });
            try {
                const categories = await fetchCategories(
                    type,
                    selectedGrade,
                    state.subject.value,
                    state.difficulty.value
                );
                dispatch({
                    type: "categories",
                    payload: categories,
                });
            } catch (error: any) {
                const errorCode = error?.message === "A002" ? "A002" : "A001";
                update("error", { show: true, code: errorCode });
            } finally {
                setLoading({ loading: false, name: "categories" });
            }
        },
        [type, state.subject.value, state.difficulty.value, dispatch, update]
    );


    const fetchCategoriesOnDifficultyChange = useCallback(
        async (difficultyValue: string) => {
            if (!state.grade.value) return;

            setLoading({ loading: true, name: "categories" });
            try {
                const categories = await fetchCategories(
                    type,
                    Number(state.grade.value),
                    state.subject.value,
                    difficultyValue
                );
                dispatch({
                    type: "categories",
                    payload: categories,
                });
            } catch (error: any) {
                // console.log("Error fetching categories:", error);
                const errorCode = error?.message === "A002" ? "A002" : "A001";
                update("error", { show: true, code: errorCode });
            } finally {
                setLoading({ loading: false, name: "categories" });
            }
        },
        [type, state.grade.value, state.difficulty.value, dispatch, update]
    );

    const setItemsHandler = useCallback(async (): Promise<boolean> => {
        const hasStandard = Number(state.grade.value) <= 8;
        const selectedCategories = getCategoriesIds(state.grade.value, state.categories, type);
        setLoading({ loading: true, name: "step" });

        try {
            const response = await APIPostItems({
                subcategories: selectedCategories,
                items_number: state.items_number.value,
                difficulty: state.difficulty.value,
                has_standard: hasStandard,
            });

            if (response.data?.success) {
                const values = response.data.data.values ?? [];
                setItems(values);
                setExcludedItems(values.map(({ id }: any) => id));
                return true;
            }
            update("error", { show: true, code: "A002" });
            return false;
        } catch {
            update("error", { show: true, code: "A001" });
            return false;
        } finally {
            setLoading({ loading: false, name: "step" });
        }
    }, [state.grade.value, state.categories, state.items_number.value, state.difficulty.value, type, update]);

    const handleItemReplace = useCallback(
        async (subcategoryId: string, id: string) => {
            const hasStandard = Number(state.grade.value) <= 8;
            try {
                const response = await APIPostItem({
                    subcategory: subcategoryId,
                    excluded_items: excludedItems,
                    difficulty: state.difficulty.value,
                    has_standard: hasStandard,
                });

                if (response.data?.success) {
                    if(response.data.data.values.length > 0) {
                        const newItem = response.data.data.values[0];
                        setItems((prevItems) => {
                            const updated = prevItems.map((obj) => (obj.id === id ? newItem : obj));
                            setExcludedItems(updated.map((item) => item.id));
                            return updated;
                        });
                    } else {
                       // console.log("No items found");
                       return;
                    }
                } else {
                    update("error", { show: true, code: "A002" });
                }
            } catch {
                update("error", { show: true, code: "A001" });
            }
        },
        [excludedItems, state.difficulty.value, update]
    );

    const setCustomItemsHandler = useCallback((item: any) => {
        setItems((prev) => [...prev, item]);
    }, []);

    const removeCustomItemHandler = useCallback((item: any) => {
        setItems((prev) => prev.filter((customItem) => customItem.id !== item.id));
    }, []);

    const clearItems = useCallback(() => {
        setItems([]);
        setExcludedItems([]);
    }, []);



    return {
        tags,
        subjects,
        grades,
        items,
        excludedItems,
        loading,
        setLoading,
        setSubjectHandler,
        setGradeHandler,
        fetchCategoriesOnDifficultyChange,
        setItemsHandler,
        handleItemReplace,
        setCustomItemsHandler,
        removeCustomItemHandler,
        clearItems,
    };

}

export default UseTestFormDataFetching;